import React, { useEffect } from "react";
import { Button } from "src/business-types/grocery/components/Button";
import DropdownSelect from "src/business-types/grocery/components/DropdownSelect";
import InputBox from "src/business-types/grocery/components/InputBox";
import { useConfigurationState } from "./hooks/useConfigurationState";
import { dropDownValue, billNoDropDownValue, dateFormatOptions, timeFormatOptions } from "./types/configuration.types";
import { PageTitle } from "../PageTitle";

const TableBillingTab = () => {
  const {
    user,
    tableRows,
    setTableRows,
    billNoEnabled,
    setBillNoEnabled,
    billNo,
    setBillNo,
    deliveryFeeEnabled,
    setDeliveryFeeEnabled,
    parcelFeeEnabled,
    setParcelFeeEnabled,
    tipsEnabled,
    setTipsEnabled,
    orderIdEnabled,
    setOrderIdEnabled,
    clientIdEnabled,
    setClientIdEnabled,
    businessName,
    setBusinessName,
    saveTableBilling,
    productEditEnabled,
    setProductEditEnabled,
    dateFormat,
    setDateFormat,
    timeFormat,
    setTimeFormat,
  } = useConfigurationState();

  const [initialState, setInitialState] = React.useState({
    tableRows,
    billNoEnabled,
    billNo,
    deliveryFeeEnabled,
    orderIdEnabled,
    clientIdEnabled,
    businessName,
    parcelFeeEnabled,
    tipsEnabled,
    productEditEnabled,
    dateFormat,
    timeFormat,
  });

  // Set orderIdEnabled to true by default when component mounts if neither is enabled
  useEffect(() => {
    if (orderIdEnabled === false && clientIdEnabled === false && initialState.orderIdEnabled === false && initialState.clientIdEnabled === false) {
      setOrderIdEnabled(true);
    }
  }, []);

  // Handle Order ID toggle - when enabling Order ID, disable Client ID
  const handleOrderIdToggle = () => {
    if (!orderIdEnabled) {
      // Enabling Order ID
      setOrderIdEnabled(true);
      setClientIdEnabled(false); // Disable Client ID
    } else {
      // Disabling Order ID
      setOrderIdEnabled(false);
    }
  };

  // Handle Client ID toggle - when enabling Client ID, disable Order ID
  const handleClientIdToggle = () => {
    if (!clientIdEnabled) {
      // Enabling Client ID
      setClientIdEnabled(true);
      setOrderIdEnabled(false); // Disable Order ID
    } else {
      // Disabling Client ID
      setClientIdEnabled(false);
    }
  };

  const handleSave = () => {
    saveTableBilling({
      tableRows,
      billNoEnabled,
      billNo,
      deliveryFeeEnabled,
      orderIdEnabled,
      clientIdEnabled,
      businessName,
      parcelFeeEnabled,
      tipsEnabled,
      productEditEnabled,
      dateFormat,
      timeFormat,
    });

    // Reset state after saving
    setInitialState({
      tableRows,
      billNoEnabled,
      billNo,
      deliveryFeeEnabled,
      orderIdEnabled,
      clientIdEnabled,
      businessName,
      parcelFeeEnabled,
      tipsEnabled,
      productEditEnabled,
      dateFormat,
      timeFormat,
    });
  };

  useEffect(() => {
    setInitialState({
      tableRows,
      billNoEnabled,
      billNo,
      deliveryFeeEnabled,
      orderIdEnabled,
      clientIdEnabled,
      businessName,
      parcelFeeEnabled,
      tipsEnabled,
      productEditEnabled,
      dateFormat,
      timeFormat,
    });
  }, [user]);

  const showParcelFee = user?.businessType === "Restaurant" || 
                     user?.businessType === "Grocery"

  // Check if any of billNoEnabled or orderIdEnabled or clientIdEnabled is true
  const isAnyFeatureEnabled = billNoEnabled || orderIdEnabled || clientIdEnabled;

  const isChanged =
    tableRows !== initialState.tableRows ||
    billNoEnabled !== initialState.billNoEnabled ||
    billNo !== initialState.billNo ||
    deliveryFeeEnabled !== initialState.deliveryFeeEnabled ||
    orderIdEnabled !== initialState.orderIdEnabled ||
    clientIdEnabled !== initialState.clientIdEnabled ||
    businessName !== initialState.businessName ||
    parcelFeeEnabled !== initialState.parcelFeeEnabled ||
    tipsEnabled !== initialState.tipsEnabled ||
    productEditEnabled !== initialState.productEditEnabled ||
    dateFormat !== initialState.dateFormat ||
    timeFormat !== initialState.timeFormat;

  // Button should be enabled only if there are changes AND at least one feature (bill number or order ID or client ID) is enabled
  const isSaveButtonEnabled = isChanged && isAnyFeatureEnabled;

  useEffect(() => {
    if (user?.businessType !== "Grocery") {
      document.title = "TableBilling› Configuration › iMetanic";
    }
  }, []);

  // Check if any ID type is enabled along with bill number
  const isAnyIdEnabled = orderIdEnabled || clientIdEnabled;
  const checkOrderBillNo = isAnyIdEnabled && billNoEnabled;

  const getDatePreview = (format: string) => {
    const now = new Date();

    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();

    switch (format) {
      case "DD/MM/YYYY":
        return `${dd}/${mm}/${yyyy}`;
      case "MM/DD/YYYY":
        return `${mm}/${dd}/${yyyy}`;
      case "YYYY/MM/DD":
        return `${yyyy}/${mm}/${dd}`;
      case "DD-MM-YYYY":
        return `${dd}-${mm}-${yyyy}`;
      case "MM-DD-YYYY":
        return `${mm}-${dd}-${yyyy}`;
      case "YYYY-MM-DD":
        return `${yyyy}-${mm}-${dd}`;
      default:
        return `${dd}/${mm}/${yyyy}`;
    }
  };

  // Function to format time preview
  const getTimePreview = (format: string) => {
    const now = new Date();
    if (format === "12h") {
      return now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
    } else {
      return now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
    }
  };

  return (
    <div className="w-full space-y-6 p-4 overflow-y-auto">
      <PageTitle title=" - Configuration › TableBilling" />
      <div className="border p-6 rounded-xl shadow bg-white space-y-6">
        {/* ORDER / BILL CONFIG */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Order ID / Client ID / Bill Number Configuration</h2>

          {/* Show Order ID */}
          <label className="flex items-start gap-2 cursor-pointer mb-3">
            <input type="checkbox" checked={orderIdEnabled} onChange={handleOrderIdToggle} className="w-4 h-4 mt-1 accent-blue-600" disabled={!checkOrderBillNo && orderIdEnabled} />
            <div>
              <span className="font-medium">Show Order ID</span>
              {!orderIdEnabled && <p className="text-xs text-gray-500">Enable order ID to show order IDs on bills and receipts</p>}
            </div>
          </label>

          {/* Show Client ID */}
          <label className="flex items-start gap-2 cursor-pointer mb-3">
            <input type="checkbox" checked={clientIdEnabled} onChange={handleClientIdToggle} className="w-4 h-4 mt-1 accent-blue-600" disabled={!checkOrderBillNo && clientIdEnabled} />
            <div>
              <span className="font-medium">Show Client ID</span>
              {!clientIdEnabled && <p className="text-xs text-gray-500">Enable client ID to show client IDs on bills and receipts</p>}
            </div>
          </label>

          {/* Info message about mutual exclusivity */}
          {(orderIdEnabled || clientIdEnabled) && <p className="text-xs text-blue-600 mt-1 mb-3">ℹ️ Only one ID type can be active at a time. Enabling one will automatically disable the other.</p>}

          <div className="border-t"></div>

          {/* Bill Reset */}
          <label className="flex items-start gap-2 cursor-pointer mb-4 mt-4">
            <input type="checkbox" checked={billNoEnabled} onChange={() => setBillNoEnabled(!billNoEnabled)} className="w-4 h-4 mt-1" disabled={!checkOrderBillNo && billNoEnabled} />
            <div>
              <span className="font-medium">Enable Bill Number </span>
              {!billNoEnabled && <p className="text-xs text-gray-500">Enable bill number reset to select a reset period</p>}
            </div>
          </label>

          {/* Dropdown */}
          <div className={`${!billNoEnabled ? "opacity-50 pointer-events-none" : ""}`}>
            <label className="block text-sm font-medium mb-2">
              Bill Number Reset Period
              <span className="text-gray-400 ml-2">(No reset / Day / Week / Month / Year)</span>
            </label>

            <div className="max-w-xs">
              <DropdownSelect titleofSelect="Bill No Reset Period" name="billNo" value={billNo} defultSelect={false} options={billNoDropDownValue} onChange={(e) => setBillNo(e as any)} />
            </div>
          </div>

          <div className="border-t my-4"></div>

          {/* Validation */}
          {!isAnyFeatureEnabled && <p className="text-xs text-red-500 mt-4 font-medium">❌ At least one feature (Bill Number Reset, Order ID, or Client ID) must be enabled</p>}
        </div>

        {/* DIVIDER */}
        <div className="border-t"></div>

        {user?.businessType === "Laundry" && (
          <>
            <div>
              <h2 className="text-lg font-semibold mb-4">Product Edit Enabled</h2>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={productEditEnabled} onChange={() => setProductEditEnabled(!productEditEnabled)} className="w-4 h-4 mt-1" />
                <div>
                  <span className="font-medium">Enable Product Edit</span>
                  {!productEditEnabled && <p className="text-xs text-gray-500">Enable product edit to allow editing product details in the cart</p>}
                </div>
              </label>
            </div>
            <div className="border-t"></div>
          </>
        )}

        {showParcelFee && (
          <>
            <div>
              <h2 className="text-lg font-semibold mb-4">Parcel Fee</h2>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={parcelFeeEnabled} onChange={() => setParcelFeeEnabled(!parcelFeeEnabled)} className="w-4 h-4 mt-1" />
                <div>
                  <span className="font-medium">Enable Parcel Fee</span>
                  {!parcelFeeEnabled && <p className="text-xs text-gray-500">Enable parcel fee in printer</p>}
                </div>
              </label>
            </div>
            <div className="border-t"></div>
          </>
        )}

        {user?.businessType === "Restaurant" && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Tips</h2>

            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={tipsEnabled} onChange={() => setTipsEnabled(!tipsEnabled)} className="w-4 h-4 mt-1" />

              <div>
                <span className="font-medium">Enable Tips</span>
                {!tipsEnabled && <p className="text-xs text-gray-500">Enable tips in printer</p>}
              </div>
            </label>
          </div>
        )}
      </div>

      {/* Date and Time Format Configuration */}
      <div className="border p-6 rounded-xl shadow bg-white">
        <h2 className="text-lg font-semibold mb-4">Date and Time Format Configuration</h2>
        {/* Date & Time Format - Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Date Format */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Date Format
              <span className="text-gray-400 ml-2">(Select your preferred date format)</span>
            </label>
            <div className="max-w-xs">
              <DropdownSelect titleofSelect="Date Format" name="dateFormat" value={dateFormat} defultSelect={false} options={dateFormatOptions} onChange={(e) => setDateFormat(e as any)} />
            </div>
            <div className="mt-2 text-sm text-gray-500">
              <span>Preview: </span>
              <span className="font-medium text-gray-700">
                {getDatePreview(dateFormat)} ({dateFormat})
              </span>
            </div>
          </div>

          {/* Time Format */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Time Format
              <span className="text-gray-400 ml-2">(Select your preferred time format)</span>
            </label>
            <div className="max-w-xs">
              <DropdownSelect titleofSelect="Time Format" name="timeFormat" value={timeFormat} defultSelect={false} options={timeFormatOptions} onChange={(e) => setTimeFormat(e as any)} />
            </div>
            <div className="mt-2 text-sm text-gray-500">
              <span>Preview: </span>
              <span className="font-medium text-gray-700">
                {getTimePreview(timeFormat)} ({timeFormat === "12h" ? "12 Hours" : "24 Hours"})
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Table Configuration */}
      <div className="border p-6 rounded-xl shadow bg-white">
        <h2 className="text-lg font-semibold mb-4">Table Configuration</h2>

        <div>
          <label className="block text-sm font-medium mb-2">
            Table Data Shown (Rows)
            <span className="text-gray-400 ml-2">(10 / 25 / 50 / 100 / 250 / 500)</span>
          </label>
          <div className="max-w-xs">
            <DropdownSelect
              name="tableRows"
              titleofSelect="Table Data Shown"
              value={String(tableRows)}
              defultSelect={false}
              options={dropDownValue}
              onChange={(value) => setTableRows(Number(value))}
            />
          </div>
        </div>
      </div>

      {/* Custom Business Name - Grocery Only */}
      {user?.businessType === "Grocery" && (
        <div className="border p-6 rounded-xl shadow bg-white">
          <h2 className="text-lg font-semibold mb-4">Custom Business Name</h2>

          <div className="max-w-xs">
            <label className="block text-sm font-medium mb-2">Business Name</label>
            <InputBox placeholder="Enter business name" onChange={(e) => setBusinessName(e.target.value)} value={businessName} />
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-center">
        <Button label="Save Table & Billing Settings" onClick={handleSave} disabled={!isSaveButtonEnabled} />
      </div>

      {/* Show message when button is disabled due to no feature enabled */}
      {!isAnyFeatureEnabled && isChanged && <div className="text-center text-sm text-red-500 mt-2">Please enable either "Bill Number Reset", "Order ID", or "Client ID" to save changes</div>}
    </div>
  );
};

export default TableBillingTab;
